import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoginCredentials, FormErrors } from '../types';
import { validateLoginForm, hasErrors } from '../utils/validation';
import { getOnboardingEntryPoint } from '../../welcome/utils/onboardingRouter';
import { AuthLayout, InputField, Checkbox } from '../../components/modern';
import { setFlowData } from '../../hooks/useFlowProtection';

const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isLoading, error, clearError, isAuthenticated } = useAuth();

    const [credentials, setCredentials] = useState<LoginCredentials>({
        email: '',
        password: '',
        rememberMe: false,
    });

    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [_successMessage, setSuccessMessage] = useState('');

    // Redirect if already authenticated - role-based redirect
    useEffect(() => {
        const handleAuthenticatedRedirect = async () => {
            if (isAuthenticated) {
                // Get user data from localStorage to determine role
                const userDataString = localStorage.getItem('authUser');
                let redirectTo = '/welcome/intro'; // default to candidate onboarding

                if (userDataString) {
                    try {
                        const userData = JSON.parse(userDataString);
                        const userRole = userData.userType || userData.role;

                        // Role-based redirect
                        switch (userRole) {
                            case 'hush_admin':
                                // Admin users go directly to admin dashboard
                                redirectTo = '/admin/dashboard';
                                break;
                            case 'company':
                                // Check if company user has completed profile setup
                                const isCompanyProfileCompleted = userData.profile_completed || userData.profileCompleted;
                                if (isCompanyProfileCompleted) {
                                    redirectTo = '/companies/dashboard';
                                } else {
                                    // Redirect to company profile setup (onboarding)
                                    // Set flow protection data so the page allows access
                                    console.log('[SignIn] Company profile not completed, redirecting to profile setup');
                                    setFlowData('fromCompanySignup', {
                                        email: userData.email,
                                        fromLogin: true
                                    });
                                    redirectTo = '/auth/company/profile-setup';
                                }
                                break;
                            case 'recruiter':
                                redirectTo = '/companies/dashboard';
                                break;
                            case 'candidate':
                            default:
                                // Check if candidate has completed onboarding
                                const isProfileCompleted = userData.profile_completed || userData.profileCompleted;
                                if (isProfileCompleted) {
                                    redirectTo = '/candidate/dashboard';
                                } else {
                                    // Fetch onboarding status to determine correct entry point
                                    // Uses /me endpoint with JWT auth - no user ID needed
                                    console.log('[SignIn] Fetching onboarding entry point using /me endpoint');
                                    try {
                                        redirectTo = await getOnboardingEntryPoint();
                                        console.log('[SignIn] Redirecting to:', redirectTo);

                                        // Set flow protection data based on the redirect destination
                                        // This allows users who login with incomplete profiles to continue onboarding
                                        if (redirectTo.includes('/welcome/')) {
                                            const flowData = { email: userData.email, fromLogin: true };

                                            if (redirectTo === '/welcome/intro') {
                                                setFlowData('fromSignup', flowData);
                                                setFlowData('fromCandidateSignup', flowData);
                                            } else if (redirectTo === '/welcome/resume-upload') {
                                                setFlowData('fromOnboarding', flowData);
                                                setFlowData('fromWelcomeIntro', flowData);
                                            } else if (redirectTo === '/welcome/profile-review') {
                                                setFlowData('fromResumeConfirmation', flowData);
                                            } else if (redirectTo === '/welcome/career-goals') {
                                                setFlowData('fromProfileReview', flowData);
                                            } else if (redirectTo === '/welcome/onboarding-chat') {
                                                setFlowData('fromCareerGoals', flowData);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('[SignIn] Failed to get onboarding entry point:', error);
                                        // Set fallback flow data for intro page
                                        setFlowData('fromSignup', { email: userData.email, fromLogin: true });
                                        setFlowData('fromCandidateSignup', { email: userData.email, fromLogin: true });
                                        redirectTo = '/welcome/intro'; // Fallback
                                    }
                                }
                                break;
                        }
                    } catch (error) {
                        console.error('Failed to parse user data:', error);
                        redirectTo = '/welcome/intro'; // Safe fallback
                    }
                }

                // Override with location state if provided
                redirectTo = location.state?.from || redirectTo;
                navigate(redirectTo, { replace: true });
            }

            // Handle success message from password reset
            if (location.state?.message) {
                setSuccessMessage(location.state.message);
            }
        };

        handleAuthenticatedRedirect();
    }, [isAuthenticated, navigate, location]);

    const handleInputChange = (field: keyof LoginCredentials, value: string | boolean) => {
        setCredentials(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (fieldErrors[field as keyof FormErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }

        // Clear general error
        if (error) {
            clearError();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const errors = validateLoginForm(credentials);
        setFieldErrors(errors);

        if (hasErrors(errors)) {
            return;
        }

        try {
            await login(credentials);
            // Navigation will happen via useEffect when isAuthenticated changes
        } catch (err) {
            // Error is handled by useAuth hook, display specific field errors if applicable
            if (err instanceof Error) {
                // Check for specific error types
                if (err.message.toLowerCase().includes('invalid') ||
                    err.message.toLowerCase().includes('password') ||
                    err.message.toLowerCase().includes('credentials')) {
                    // Clear any specific field errors since this is a general auth error
                    setFieldErrors({});
                }
            }
            console.error('Login failed:', err);
        }
    };

    return (
        <AuthLayout
            title="Sign in"
            subtitle={
                <>
                    Don't have an account? <a href="/auth/apply" className="text-[#015FA6] font-bold underline decoration-2 underline-offset-4 hover:text-[#043362] transition-colors">Create now</a>
                </>
            }
        >
            <form className="flex flex-col gap-6 w-full" onSubmit={handleSubmit}>

                <InputField
                    id="email"
                    label="Email"
                    type="email"
                    placeholder="example@gmail.com"
                    value={credentials.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={fieldErrors.email}
                    delay={200}
                />

                <div className="flex flex-col gap-1">
                    <InputField
                        id="password"
                        label="Password"
                        type="password"
                        placeholder="@#*%"
                        value={credentials.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        error={fieldErrors.password}
                        delay={300}
                    />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2 animate-fade-in-up opacity-0" style={{ animationDelay: '400ms' }}>
                    <Checkbox
                        label="Remember me"
                        checked={credentials.rememberMe}
                        onChange={(checked) => handleInputChange('rememberMe', checked)}
                    />
                    <a href="/auth/forgot-password" className="text-[#015FA6] font-semibold text-sm hover:text-[#043362] transition-colors hover:underline underline-offset-2">
                        Forgot Password?
                    </a>
                </div>

                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-fade-in-up opacity-0" style={{ animationDelay: '450ms' }}>
                        <p className="text-sm text-red-600 font-manrope">{error}</p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={isLoading}
                    className="
                        w-full h-[60px] mt-6 rounded-xl font-bold text-lg text-white
                        bg-gradient-to-r from-[#015FA6] to-[#0172C6]
                        hover:shadow-[0_8px_25px_-5px_rgba(1,95,166,0.4)]
                        active:scale-[0.98]
                        transition-all duration-300
                        animate-fade-in-up opacity-0
                        flex items-center justify-center gap-2
                        disabled:opacity-70 disabled:cursor-not-allowed
                    "
                    style={{ animationDelay: '500ms' }}
                >
                    {isLoading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Signing in...
                        </>
                    ) : (
                        'Sign in'
                    )}
                </button>

            </form>
        </AuthLayout>
    );
};

export default SignIn;
